import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize, RotateCw, Pause, DoorOpen } from "lucide-react";
import { loadPannellum } from "./PanoViewer";

const U = (id, w = 3600) =>
    `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=${w}&h=${w / 2}&fit=crop`;

const SCENES = {
    lobby: {
        label: "Grand Lobby",
        image: U("photo-1747380755783-61befe9b3da7"),
        hotspots: [
            { pitch: -3, yaw: 55, type: "scene", sceneId: "suite", text: "Walk into the VIP Suite" },
            { pitch: -4, yaw: -60, type: "scene", sceneId: "restaurant", text: "Head to the Restaurant" },
            { pitch: 2, yaw: 170, type: "info", text: "Reception — first thing your customers see" },
        ],
    },
    suite: {
        label: "VIP Suite",
        image: U("photo-1760072513357-9d450e935a80"),
        hotspots: [
            { pitch: -3, yaw: -140, type: "scene", sceneId: "lobby", text: "Back to the Grand Lobby" },
            { pitch: -2, yaw: 30, type: "info", text: "Panoramic windows — highlight what matters" },
        ],
    },
    restaurant: {
        label: "Restaurant",
        image: U("photo-1733413788595-ab2e2e8c0a9c"),
        hotspots: [
            { pitch: -4, yaw: 120, type: "scene", sceneId: "lobby", text: "Back to the Grand Lobby" },
            { pitch: 0, yaw: -20, type: "info", text: "Ambience sells — let guests feel it before they book" },
        ],
    },
};

export default function SceneTour({
    testIdPrefix = "scene-tour",
    heightClass = "h-[58vh] min-h-[400px]",
    autoRotate = -1.4,
    className = "",
}) {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const [rotating, setRotating] = useState(true);
    const [scene, setScene] = useState("lobby");

    useEffect(() => {
        let destroyed = false;
        loadPannellum()
            .then(() => {
                if (destroyed || !containerRef.current) return;
                try {
                    const scenes = {};
                    Object.entries(SCENES).forEach(([id, s]) => {
                        scenes[id] = {
                            type: "equirectangular",
                            panorama: s.image,
                            hfov: 105,
                            hotSpots: s.hotspots.map((h) => ({
                                pitch: h.pitch,
                                yaw: h.yaw,
                                type: h.type,
                                text: h.text,
                                sceneId: h.sceneId,
                                cssClass: "custom-hotspot",
                            })),
                        };
                    });
                    viewerRef.current = window.pannellum.viewer(containerRef.current, {
                        default: {
                            firstScene: "lobby",
                            autoLoad: true,
                            autoRotate,
                            autoRotateInactivityDelay: 1800,
                            sceneFadeDuration: 1400,
                            compass: false,
                            showControls: false,
                        },
                        scenes,
                    });
                    viewerRef.current.on("load", () => {
                        if (!destroyed) setReady(true);
                    });
                    viewerRef.current.on("scenechange", (id) => {
                        if (!destroyed) setScene(id);
                    });
                    viewerRef.current.on("error", () => {
                        if (!destroyed) setFailed(true);
                    });
                } catch (e) {
                    setFailed(true);
                }
            })
            .catch(() => setFailed(true));
        return () => {
            destroyed = true;
            try {
                viewerRef.current && viewerRef.current.destroy();
            } catch (e) {
                /* noop */
            }
            viewerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const zoom = (dir) => {
        const v = viewerRef.current;
        if (!v) return;
        v.setHfov(Math.min(120, Math.max(45, v.getHfov() + dir * 12)));
    };

    const toggleRotate = () => {
        const v = viewerRef.current;
        if (!v) return;
        if (rotating) {
            v.stopAutoRotate?.();
        } else {
            v.startAutoRotate?.(autoRotate);
        }
        setRotating(!rotating);
    };

    const goScene = (id) => {
        viewerRef.current && viewerRef.current.loadScene(id);
    };

    return (
        <div
            className={`group relative overflow-hidden rounded-2xl border border-[#00C6FF]/25 shadow-[0_0_50px_rgba(0,198,255,0.12)] ${className}`}
            data-testid={`${testIdPrefix}-viewer`}
        >
            <div ref={containerRef} className={`w-full ${heightClass} bg-[#0A1F44]`} />

            {!ready && !failed && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#050B14]/90">
                    <div className="h-12 w-12 animate-spin rounded-full border-2 border-[#00C6FF]/20 border-t-[#00C6FF]" />
                    <p className="font-mono-accent text-xs tracking-[0.3em] text-[#00C6FF]">
                        BUILDING WALKTHROUGH
                    </p>
                </div>
            )}

            {failed && (
                <div className="absolute inset-0">
                    <img src={SCENES.lobby.image} alt="360° preview" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-transparent to-transparent" />
                </div>
            )}

            <div className="pointer-events-none absolute left-4 top-4 z-10 flex items-center gap-2 rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 px-4 py-2 backdrop-blur-md">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#00C6FF]" />
                <span className="font-mono-accent text-[10px] tracking-[0.3em] text-[#00C6FF]">
                    LIVE WALKTHROUGH
                </span>
            </div>

            <div className="absolute left-1/2 top-4 z-10 flex -translate-x-1/2 gap-2">
                {Object.entries(SCENES).map(([id, s]) => (
                    <button
                        key={id}
                        data-testid={`${testIdPrefix}-scene-${id}`}
                        onClick={() => goScene(id)}
                        className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] backdrop-blur-md transition-all duration-300 ${
                            scene === id
                                ? "bg-[#00C6FF] text-[#050B14]"
                                : "border border-[#00C6FF]/30 bg-[#050B14]/70 text-slate-300 hover:text-[#00C6FF]"
                        }`}
                    >
                        <DoorOpen size={12} />
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="pointer-events-none absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full border border-white/10 bg-[#050B14]/60 px-5 py-2 text-xs tracking-widest text-slate-300 backdrop-blur-md transition-opacity duration-500 group-hover:opacity-0">
                TAP A GLOWING DOT TO MOVE ROOMS
            </div>

            <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-2">
                <button data-testid={`${testIdPrefix}-zoom-in`} onClick={() => zoom(-1)} aria-label="Zoom in"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]">
                    <ZoomIn size={16} />
                </button>
                <button data-testid={`${testIdPrefix}-zoom-out`} onClick={() => zoom(1)} aria-label="Zoom out"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]">
                    <ZoomOut size={16} />
                </button>
                <button data-testid={`${testIdPrefix}-autorotate-toggle`} onClick={toggleRotate} aria-label="Toggle auto rotate"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]">
                    {rotating ? <Pause size={16} /> : <RotateCw size={16} />}
                </button>
                <button data-testid={`${testIdPrefix}-fullscreen`} onClick={() => viewerRef.current?.toggleFullscreen()} aria-label="Fullscreen"
                    className="rounded-full border border-[#00C6FF]/30 bg-[#050B14]/70 p-2.5 text-[#00C6FF] backdrop-blur-md transition-colors hover:bg-[#00C6FF] hover:text-[#050B14]">
                    <Maximize size={16} />
                </button>
            </div>
        </div>
    );
}
